// components/InPostMap.tsx
import React, { useEffect, useRef, useState } from "react";
import { convertToInPostPoint } from "../../../types/deliveryPoints.type";

// Typ dla oryginalnej odpowiedzi API InPost (snake_case)

export const InPostMap = ({
  onPointSelected,
  isVisible,
  onClose
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isScriptsLoaded, setIsScriptsLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const containerRef = useRef(null);
  const geowidgetRef = useRef(null);

  // Konfiguracja środowiska produkcyjnego
  const PRODUCTION_BASE_URL = "https://geowidget.inpost.pl";
  const PRODUCTION_TOKEN = "eyJhbGciOiJSUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICJzQlpXVzFNZzVlQnpDYU1XU3JvTlBjRWFveFpXcW9Ua2FuZVB3X291LWxvIn0.eyJleHAiOjIwNzIwNDUwMTEsImlhdCI6MTc1NjY4NTAxMSwianRpIjoiNDJiMTY3ZGEtODAxNy00ZTk1LWE1NDUtYWY1ZWExMDg3NjMyIiwiaXNzIjoiaHR0cHM6Ly9sb2dpbi5pbnBvc3QucGwvYXV0aC9yZWFsbXMvZXh0ZXJuYWwiLCJzdWIiOiJmOjEyNDc1MDUxLTFjMDMtNGU1OS1iYTBjLTJiNDU2OTVlZjUzNTp5Z2FpZWpJYUlwTWhva3REeU1LazVnIiwidHlwIjoiQmVhcmVyIiwiYXpwIjoic2hpcHgiLCJzZXNzaW9uX3N0YXRlIjoiNjg3ODgzMDUtYzAyNi00OGVkLTlhODctZGY3NGYzYmJkMjJiIiwic2NvcGUiOiJvcGVuaWQgYXBpOmFwaXBvaW50cyIsInNpZCI6IjY4Nzg4MzA1LWMwMjYtNDhlZC05YTg3LWRmNzRmM2JiZDIyYiIsImFsbG93ZWRfcmVmZXJyZXJzIjoiKi5jYXJyeS5wbCIsInV1aWQiOiJjOTI2MDE1Zi1kZWY5LTQyZDYtOWNmNC0wOTc5ZmZkMzNjYzIifQ.l0gWChiPO7C_6rPi5D_2-HUQ0Txo9t7Kt92OPGnw0tAyY14DzjNy0xJTe5GsORphqj0kfovTR9uRrcXughycdLB2DH56XNCtbc6IZAhpDme3qNSbcPi8pq82Rzohnmk7aBAOYX5oQeIavHhJiliTeVx1LsXHb4pr6oDqaSGpJFoK1pECvilbsy0h2AO4TNz6XQaIzEEsuXyLLZBmfoOMqUTFb6I4m6nq0f2W00GV7FiIddaCOQPN87OJoO9E99o9ekAv9fKNH-tzmnJ3a2GL3-Orrxdk6dHD3xNGzRkJSAur5GuYQz68Hniyo1gxSsExlYbnK78FZRZDa8tBX1-b9w";

  // Helper function to convert snake_case response to camelCase
  const handlePointSelection = apiPoint => {
    try {
      const convertedPoint = convertToInPostPoint(apiPoint);
      console.log("Converted InPost point:", convertedPoint);
      onPointSelected(convertedPoint);
      onClose(); // Automatycznie zamknij modal po wyborze
      setIsLoading(false);
    } catch (error) {
      console.error("Error converting InPost point:", error);
      setIsLoading(false);
    }
  };

  // Załaduj skrypty InPost Geowidget v5 - tylko gdy modal jest otwarty
  useEffect(() => {
    if (!isVisible) return;
    const loadInPostScripts = () => {
      // Sprawdź czy już są załadowane
      const existingCSS = document.querySelector(`link[href*="${PRODUCTION_BASE_URL}"]`);
      const existingJS = document.querySelector(`script[src*="${PRODUCTION_BASE_URL}"]`);
      if (existingCSS && existingJS && window.customElements?.get("inpost-geowidget")) {
        console.log("InPost scripts already loaded");
        // Dodaj małe opóźnienie aby DOM się ustabilizował
        setTimeout(() => setIsScriptsLoaded(true), 100);
        return;
      }
      try {
        setIsLoading(true);

        // Załaduj CSS
        if (!existingCSS) {
          const cssLink = document.createElement("link");
          cssLink.rel = "stylesheet";
          cssLink.href = `${PRODUCTION_BASE_URL}/inpost-geowidget.css`;
          cssLink.onload = () => console.log("InPost CSS załadowany");
          cssLink.onerror = () => {
            console.error("Błąd ładowania CSS InPost");
            setScriptError(true);
            setIsLoading(false);
          };
          document.head.appendChild(cssLink);
        }

        // Załaduj JS
        if (!existingJS) {
          const script = document.createElement("script");
          script.src = `${PRODUCTION_BASE_URL}/inpost-geowidget.js`;
          script.defer = true;
          script.onload = () => {
            console.log("InPost JS załadowany");
            // Poczekaj na pełne załadowanie custom elements z timeout
            let attempts = 0;
            const maxAttempts = 50; // 5 sekund

            const checkCustomElement = () => {
              if (window.customElements?.get("inpost-geowidget")) {
                console.log("InPost custom element ready");
                setIsScriptsLoaded(true);
                setScriptError(false);
                setIsLoading(false);
              } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkCustomElement, 100);
              } else {
                console.error("Timeout waiting for InPost custom element");
                setScriptError(true);
                setIsLoading(false);
              }
            };
            checkCustomElement();
          };
          script.onerror = () => {
            console.error("Nie udało się załadować skryptu InPost Geowidget");
            setScriptError(true);
            setIsLoading(false);
          };
          document.head.appendChild(script);
        } else if (window.customElements?.get("inpost-geowidget")) {
          setTimeout(() => {
            setIsScriptsLoaded(true);
            setIsLoading(false);
          }, 100);
        }
      } catch (error_0) {
        console.error("Błąd podczas ładowania skryptów InPost:", error_0);
        setScriptError(true);
        setIsLoading(false);
      }
    };
    if (!scriptError) {
      loadInPostScripts();
    }
  }, [isVisible, scriptError, PRODUCTION_BASE_URL]);

  // Inicjalizuj widget gdy jest widoczny i skrypty załadowane
  useEffect(() => {
    if (!isVisible || !isScriptsLoaded || !containerRef.current || scriptError) {
      return;
    }
    console.log("Initializing InPost widget...");
    setIsLoading(true);

    // Małe opóźnienie aby DOM się ustabilizował
    const initTimeout = setTimeout(() => {
      try {
        // Wyczyść kontener
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
        }

        // Ustaw globalną funkcję callback (fallback)
        window.afterPointSelected = point => {
          console.log("Point selected via global callback:", point);
          handlePointSelection(point);
        };

        // Utwórz element geowidget
        const geowidget = document.createElement("inpost-geowidget");
        geowidget.setAttribute("token", PRODUCTION_TOKEN);
        geowidget.setAttribute("language", "pl");
        geowidget.setAttribute("config", "parcelCollect247");
        geowidget.setAttribute("onpoint", "afterPointSelected"); // Dodaj fallback
        geowidget.id = "inpost-geowidget-instance";

        // Event listener dla wyboru punktu (główny sposób)
        const handlePointSelect = event => {
          console.log("Point selected via event:", event.detail);
          if (event.detail) {
            handlePointSelection(event.detail);
          }
        };

        // Event listener dla inicjalizacji API
        const handleInit = event_0 => {
          console.log("InPost Geowidget initialized", event_0.detail);
          setIsLoading(false);
          if (event_0.detail?.api) {
            console.log("InPost Geowidget API dostępne:", event_0.detail.api);
          }
        };

        // Dodaj event listenery PRZED dodaniem do DOM
        geowidget.addEventListener("onpointselect", handlePointSelect);
        geowidget.addEventListener("inpost.geowidget.init", handleInit);

        // Dodaj do DOM
        if (containerRef.current) {
          containerRef.current.appendChild(geowidget);
          geowidgetRef.current = geowidget;
          console.log("InPost Geowidget element utworzony i dodany do DOM");
        }
      } catch (error_1) {
        console.error("Błąd inicjalizacji InPost Geowidget:", error_1);
        setIsLoading(false);
        setScriptError(true);
      }
    }, 300); // 300ms opóźnienia

    return () => clearTimeout(initTimeout);
  }, [isVisible, isScriptsLoaded, scriptError, PRODUCTION_TOKEN]);

  // Cleanup przy zamknięciu
  useEffect(() => {
    if (!isVisible) {
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
      if (geowidgetRef.current) {
        // Usuń event listenery
        try {
          geowidgetRef.current.removeEventListener("onpointselect", () => {});
          geowidgetRef.current.removeEventListener("inpost.geowidget.init", () => {});
        } catch (e) {
          console.warn("Error removing event listeners:", e);
        }
        geowidgetRef.current = null;
      }
      if (window.afterPointSelected) {
        delete window.afterPointSelected;
      }
      setIsLoading(false);
    }
  }, [isVisible]);

  // Cleanup przy unmount
  useEffect(() => {
    return () => {
      if (window.afterPointSelected) {
        delete window.afterPointSelected;
      }
    };
  }, []);

  // Jeśli mapa nie jest widoczna, nie renderuj niczego
  if (!isVisible) return null;

  // Jeśli wystąpił błąd ładowania skryptów
  if (scriptError) {
    return <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-6 shadow-xl max-w-md">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Błąd ładowania mapy InPost
            </h3>
            <p className="text-gray-600 mb-4">
              Nie udało się załadować InPost Geowidget
            </p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => {
              setScriptError(false);
              setIsScriptsLoaded(false);
            }} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
                Spróbuj ponownie
              </button>
              <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors">
                Zamknij
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-[95%] h-[95%] max-w-[1400px] max-h-[800px] bg-white rounded-lg shadow-xl overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg font-semibold text-gray-800">
            Wybierz punkt odbioru InPost
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors ml-2" aria-label="Zamknij mapę">
            ×
          </button>
        </div>

        {isLoading && <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Ładowanie mapy InPost...</p>
              <p className="text-sm text-gray-500 mt-2">Proszę czekać...</p>
            </div>
          </div>}

        <div className="w-full h-[calc(100%-64px)] relative">
          <div ref={containerRef} className="w-full h-full" style={{
          minHeight: "500px"
        }} />
        </div>
      </div>
    </div>;
};